import { useEffect, useState } from "react";
import { Accordion } from "@asosteam/asos-web-component-library-accordion";
import styles from "./ProductDescription.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { getFeature } from "../../utils/features";
import { fireProductDetailsAccordionItemOpened } from "../../analytics/pageInteraction";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { GenericAccordionItem } from "./GenericAccordionItem";
import { ProductDetails } from "./ProductDetails";
import { MergedProductDetails } from "./MergedProductDetails";
import { AboutMe } from "./AboutMe";
import { MoreInfo } from "./MoreInfo";
import { SizeAndFit } from "./SizeAndFit";
import { useSizing } from "../../context/sizing/SizingContext";
import { showSizeAndFit } from "./sizeAndFitHelper";
import { useSustainabilityInfo } from "../sustainabilityInfo/useSustainabilityInfo";
import useFitAssistExperiment from "../fitAssistant/useFitAssistExperiment";
import ReportProductLink from "../reportProductLink/ReportProductLink";
import { useOptionalMySizeProfile } from "../../context/mySizeProfile/MySizeProfileContext";

export default function ProductDescription() {
  const {
    product: { pdpLayout, showCmaTitle },
    productDescription,
    getTranslation,
  } = useRuntimeConstants();
  const [isAccordionTrackingEnabled, setIsAccordionTrackingEnabled] =
    useState(false);
  const { areFeaturesReady } = useFeaturesReady();
  const {
    sizeGuideUrl,
    recommendedSize,
    isPartialRecommendation,
    fitAssistantLinkText,
  } = useSizing();
  const {
    isEligible: isMySizeProfileEligible,
    isAuthenticated: isMySizeProfileAuthenticated,
  } = useOptionalMySizeProfile();
  const shouldSuppressForMySizeProfile =
    isMySizeProfileEligible && isMySizeProfileAuthenticated;
  const { isSuppressed } = useFitAssistExperiment({
    fitAssistEligible: !!fitAssistantLinkText,
    shouldSuppressForMySizeProfile,
  });

  const showSustainability = useSustainabilityInfo();

  useEffect(() => {
    if (!areFeaturesReady) return;

    setIsAccordionTrackingEnabled(
      getFeature("pdp-accordion-tracking").isEnabled(),
    );
  }, [areFeaturesReady]);

  const onItemClickHandler = (_, itemClicked, isOpen) => {
    if (isOpen && isAccordionTrackingEnabled) {
      const { analyticsId } = itemClicked;
      fireProductDetailsAccordionItemOpened(analyticsId);
    }
  };

  const getAboutMeTitleTranslationKey = () => {
    switch (true) {
      case pdpLayout === "Face & Body":
        return "facebody_pdp_info_howtouse";
      case showCmaTitle:
        return "pdp_description_about_me_title_cma";
      default:
        return "pdp_description_about_me_title";
    }
  };

  const shouldShowSizeAndFit =
    showSizeAndFit({
      sizeAndFitVisible: productDescription.sizeAndFitVisible,
      sizeGuideUrl,
      recommendedSize,
      isPartialRecommendation,
    }) &&
    !(isSuppressed && !sizeGuideUrl && !productDescription.sizeAndFitVisible);

  const accordionOrdersByLayout = {
    default: ["productDescriptionSizeAndFit", "productDescriptionDetails"],
    faceAndBody: [
      "productDescriptionDetails",
      "productDescriptionBrand",
      "productDescriptionSizeAndFit",
      "productDescriptionCareInfo",
      "productDescriptionAboutMe",
      "productDescriptionMoreInfo",
    ],
  };

  return (
    <div
      className={styles.container}
      id="productDescription"
      data-testid="product-description-section"
    >
      <Accordion
        items={buildAccordionItems(
          getAccordionOrder(pdpLayout, accordionOrdersByLayout),
          productDescription,
          getTranslation,
          getAboutMeTitleTranslationKey,
          shouldUseMergedProductDetails(pdpLayout),
          shouldShowSizeAndFit,
          showSustainability,
        )}
        onItemClick={onItemClickHandler}
        headingElement="h2"
        ariaLabel={getTranslation("pdp_product_info_title")}
      />
    </div>
  );
}

function getAccordionOrder(pdpLayout, accordionOrdersByLayout) {
  return pdpLayout === "Face & Body"
    ? accordionOrdersByLayout.faceAndBody
    : accordionOrdersByLayout.default;
}

export function shouldUseMergedProductDetails(pdpLayout) {
  return pdpLayout !== "Face & Body";
}

function createProductDetailsSection(
  productDescription,
  getTranslation,
  useNewProductDetails,
) {
  return {
    id: "productDescriptionDetails",
    analyticsId: "Product Details",
    title: getTranslation("pdp_description_product_details_title"),
    ariaLabel: getTranslation("pdp_description_product_details_title"),
    component: (
      <>
        {useNewProductDetails ? (
          <MergedProductDetails productDescription={productDescription} />
        ) : (
          <ProductDetails
            productDescription={productDescription.productDescription}
            productDescriptionVisible={
              productDescription.productDescriptionVisible
            }
          />
        )}
        <ReportProductLink />
      </>
    ),
  };
}

function createGenericSection(
  id,
  analyticsId,
  titleKey,
  content,
  isVisible,
  getTranslation,
  ariaLabel,
) {
  if (!isVisible) return null;
  return {
    id,
    analyticsId,
    title: getTranslation(titleKey),
    ariaLabel,
    component: <GenericAccordionItem content={content} isVisible={isVisible} />,
  };
}

function createSizeAndFitSection(
  productDescription,
  shouldShowSizeAndFit,
  getTranslation,
) {
  if (!shouldShowSizeAndFit) return null;
  return {
    id: "productDescriptionSizeAndFit",
    analyticsId: "Size & Fit",
    title: getTranslation("pdp_description_size_and_fit_title"),
    ariaLabel: getTranslation("pdp_description_size_and_fit_title"),
    component: (
      <SizeAndFit
        sizeAndFit={productDescription.sizeAndFit}
        sizeAndFitVisible={productDescription.sizeAndFitVisible}
      />
    ),
  };
}

function createAboutMeSection(
  productDescription,
  showSustainability,
  getTranslation,
  getAboutMeTitleTranslationKey,
) {
  if (!productDescription.aboutMe && !showSustainability) return null;
  return {
    id: "productDescriptionAboutMe",
    analyticsId: "About Me",
    title: getTranslation(getAboutMeTitleTranslationKey()),
    ariaLabel: getTranslation(getAboutMeTitleTranslationKey()),
    component: (
      <AboutMe
        aboutMe={productDescription.aboutMe}
        aboutMeVisible={productDescription.aboutMeVisible}
      />
    ),
  };
}

function createMoreInfoSection(productDescription, getTranslation) {
  if (!productDescription.moreInfoVisible || !productDescription.badges?.length)
    return null;
  return {
    id: "productDescriptionMoreInfo",
    analyticsId: "More Info",
    title: getTranslation("pdp_description_more_info_title"),
    ariaLabel: getTranslation("pdp_description_more_info_title"),
    component: (
      <MoreInfo
        badges={productDescription.badges}
        moreInfoVisible={productDescription.moreInfoVisible}
      />
    ),
  };
}

function buildAccordionItems(
  order,
  productDescription,
  getTranslation,
  getAboutMeTitleTranslationKey,
  useNewProductDetails = false,
  shouldShowSizeAndFit = false,
  showSustainability,
) {
  const sections = {
    productDescriptionDetails: createProductDetailsSection(
      productDescription,
      getTranslation,
      useNewProductDetails,
    ),
    productDescriptionBrand: createGenericSection(
      "productDescriptionBrand",
      "Brand",
      "pdp_description_brand_title",
      productDescription.brandDescription,
      productDescription.brandDescriptionVisible,
      getTranslation,
      getTranslation("pdp_description_brand_title"),
    ),
    productDescriptionSizeAndFit: createSizeAndFitSection(
      productDescription,
      shouldShowSizeAndFit,
      getTranslation,
    ),
    productDescriptionCareInfo: createGenericSection(
      "productDescriptionCareInfo",
      "Care Info",
      "pdp_description_care_info_title",
      productDescription.careInfo,
      productDescription.careInfoVisible,
      getTranslation,
      getTranslation("pdp_description_care_info_title"),
    ),
    productDescriptionAboutMe: createAboutMeSection(
      productDescription,
      showSustainability,
      getTranslation,
      getAboutMeTitleTranslationKey,
    ),
    productDescriptionMoreInfo: createMoreInfoSection(
      productDescription,
      getTranslation,
    ),
  };

  return order
    .map((key) => sections[key])
    .filter((item) => item && item.component)
    .map(({ title, id, analyticsId, ariaLabel, component }) => ({
      contentId: id,
      analyticsId,
      title,
      ariaLabel,
      content: component,
    }));
}
